import { FC, useEffect, useRef, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { ImageUploadBox } from '@service/seller/components/ImageUploadBox';
import {
  giftItemBrandDataAtomFamily,
  giftItemBrandListDataAtom,
  giftItemBrandStatusAtomFamily,
} from '@shared/api/giftItemBrand/giftItemBrand.atom';
import {
  createGiftItemBrand,
  updateGiftItemBrand,
} from '@shared/api/giftItemBrand/giftItemBrand.controller';
import {
  giftItemBrandListUrl,
  giftItemBrandUrl,
  useGiftItemBrandHook,
} from '@shared/api/giftItemBrand/giftItemBrand.hook';
import { AccountEntity } from '@shared/api/accountConfig/accountConfig.interface';
import {
  SettlementInfo,
  ThanksPrintBox,
  ThanksPrintConfig,
  ThanksPrintTemplate,
} from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { Image } from '@shared/api/media/media.interface';
import { postImage } from '@shared/api/uploadImage';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import { TextField } from '@shared/components/TextField';
import { useAuth } from '@shared/state/hooks/useAuth';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import {
  ThanksPrintBoxEditor,
  ThanksPrintBoxKey,
} from '../components/ThanksPrintBoxEditor';

/** 폼 내부에서 다루는 감사인쇄 템플릿 (신규 파일 업로드 상태 포함) */
interface TemplateForm {
  key: string; // 렌더 안정용 로컬 키
  _id?: string; // 기존 템플릿 id (신규는 없음)
  name: string;
  templateImage: Image | null; // 기존 populated 이미지
  file: File | null; // 새로 선택한 파일
  preview: string; // 새 파일 object URL
  // 감사글 지정 문구 세트 — 등록 시 구매자는 이 중 하나만 선택 (자유입력 불가).
  // 줄바꿈·들여쓰기까지 인쇄 원본이므로 trim하지 않고 그대로 보존한다.
  messagePresets: string[];
  senderNameMaxLength: string; // 보내는 분 인쇄문구("이름 배상" 전체) 글자수 제한
  messageBox?: ThanksPrintBox;
  senderBox?: ThanksPrintBox;
  enabled: boolean;
}

const genKey = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `tpl-${Math.random().toString(36).slice(2)}`;

const createEmptyTemplate = (): TemplateForm => ({
  key: genKey(),
  name: '',
  templateImage: null,
  file: null,
  preview: '',
  messagePresets: [],
  senderNameMaxLength: '',
  enabled: true,
});

export const GiftItemBrandFormPage: FC = function GiftItemBrandFormPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();
  const { sellerRole, isAdmin } = useAuth();

  const isEditMode = Boolean(brandId);
  // 정산정보는 플랫폼↔공급사 정산용이라 관리자만 조회/수정
  const canEditSettlement = isAdmin();

  // 수정 모드일 때 데이터 조회
  useGiftItemBrandHook(brandId ?? '', sellerRole(), !isEditMode);
  const status = useRecoilValue(giftItemBrandStatusAtomFamily(brandId ?? ''));
  const brandData = useRecoilValue(giftItemBrandDataAtomFamily(brandId ?? ''));

  // 목록 데이터 업데이트용
  const setListData = useSetRecoilState(giftItemBrandListDataAtom);
  const queryClient = useQueryClient();

  // 폼 상태
  const [name, setName] = useState('');
  const [validPeriod, setValidPeriod] = useState('');
  const [placeOfExchange, setPlaceOfExchange] = useState('');
  const [shippingInfo, setShippingInfo] = useState('');
  const [shippingFeeInfo, setShippingFeeInfo] = useState('');
  const [shippingFee, setShippingFee] = useState(''); // 배송비 (원)
  const [freeShippingMinQuantity, setFreeShippingMinQuantity] = useState(''); // 무료배송 최소수량
  const [notifyPhoneNumbers, setNotifyPhoneNumbers] = useState<string[]>(['']);
  // 개별배송 허용 여부 (false면 일괄배송 전용). 상품에 설정이 없으면 이 값을 따름
  const [allowIndividualDelivery, setAllowIndividualDelivery] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 정산정보(settlementInfo) 상태 — 관리자 전용
  const [settlementAccountEntity, setSettlementAccountEntity] = useState<AccountEntity>(
    AccountEntity.NotDecided,
  );
  const [settlementShippingFee, setSettlementShippingFee] = useState('');
  const [settlementFreeShippingMinQuantity, setSettlementFreeShippingMinQuantity] =
    useState('');
  const [settlementCourierFee, setSettlementCourierFee] = useState('');
  const [settlementCourierFreeMinQuantity, setSettlementCourierFreeMinQuantity] =
    useState('');
  const [settlementDay, setSettlementDay] = useState('');

  // 감사메시지 인쇄(thanksPrint) 상태
  const [thanksEnabled, setThanksEnabled] = useState(false);
  const [thanksGuideText, setThanksGuideText] = useState('');
  const [templates, setTemplates] = useState<TemplateForm[]>([]);

  // 컴포넌트 언마운트 시 생성한 object URL 정리
  useEffect(() => {
    return () => {
      templates.forEach((t) => t.preview && URL.revokeObjectURL(t.preview));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 템플릿 부분 업데이트 헬퍼
  const updateTemplate = (key: string, patch: Partial<TemplateForm>) => {
    setTemplates((prev) => prev.map((t) => (t.key === key ? { ...t, ...patch } : t)));
  };

  // 지정 문구 세트 편집 헬퍼
  const updatePreset = (key: string, idx: number, value: string) => {
    setTemplates((prev) =>
      prev.map((t) =>
        t.key === key
          ? {
              ...t,
              messagePresets: t.messagePresets.map((p, i) => (i === idx ? value : p)),
            }
          : t,
      ),
    );
  };
  const addPreset = (key: string) => {
    setTemplates((prev) =>
      prev.map((t) =>
        t.key === key ? { ...t, messagePresets: [...t.messagePresets, ''] } : t,
      ),
    );
  };
  const removePreset = (key: string, idx: number) => {
    setTemplates((prev) =>
      prev.map((t) =>
        t.key === key
          ? { ...t, messagePresets: t.messagePresets.filter((_, i) => i !== idx) }
          : t,
      ),
    );
  };

  // 템플릿 이미지 파일 선택 (object URL 미리보기 생성)
  const handleTemplateFile = (key: string, file: File) => {
    const url = URL.createObjectURL(file);
    setTemplates((prev) =>
      prev.map((t) => {
        if (t.key !== key) return t;
        if (t.preview) URL.revokeObjectURL(t.preview);
        return { ...t, file, preview: url };
      }),
    );
  };

  // 박스 좌표 변경
  const handleBoxChange = (
    key: string,
    boxKey: ThanksPrintBoxKey,
    box: ThanksPrintBox,
  ) => {
    updateTemplate(key, boxKey === 'message' ? { messageBox: box } : { senderBox: box });
  };

  const addTemplate = () => setTemplates((prev) => [...prev, createEmptyTemplate()]);

  const removeTemplate = (key: string) => {
    setTemplates((prev) => {
      const target = prev.find((t) => t.key === key);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return prev.filter((t) => t.key !== key);
    });
  };

  // 에디터에 표시할 템플릿 이미지 URL (새 파일 우선, 없으면 기존)
  const templateDisplayUrl = (t: TemplateForm) =>
    t.preview || (t.templateImage ? fileBucketUrl(t.templateImage.url, 'raw') : '');

  // 수정 모드일 때 기존 데이터 로드 — 최초 1회만 폼을 채운다.
  // 창 포커스 등으로 react-query가 refetch하면(refetchOnWindowFocus 기본 true) 원자
  // 참조가 바뀌어 이 effect가 다시 돌지만, 이미 초기화됐다면 입력 중인 값을 덮어쓰지
  // 않는다. (저장 후에는 목록으로 navigate하므로 in-place 재반영은 불필요.)
  const didInitRef = useRef(false);
  useEffect(() => {
    if (isEditMode && brandData && !didInitRef.current) {
      didInitRef.current = true;
      setName(brandData.name);
      setValidPeriod(brandData.couponDetail?.validPeriod ?? '');
      setPlaceOfExchange(brandData.couponDetail?.placeOfExchange ?? '');
      setShippingInfo(brandData.deliveryDetail?.shippingInfo ?? '');
      setShippingFeeInfo(brandData.deliveryDetail?.shippingFeeInfo ?? '');
      setShippingFee(brandData.deliveryDetail?.shippingFee?.toString() ?? '');
      setFreeShippingMinQuantity(
        brandData.deliveryDetail?.freeShippingMinQuantity?.toString() ?? '',
      );
      const phones = brandData.deliveryDetail?.notifyPhoneNumbers;
      setNotifyPhoneNumbers(phones && phones.length > 0 ? [...phones] : ['']);
      setAllowIndividualDelivery(brandData.allowIndividualDelivery ?? true);

      const si = brandData.settlementInfo;
      setSettlementAccountEntity(si?.accountEntity ?? AccountEntity.NotDecided);
      setSettlementShippingFee(si?.shippingFee?.toString() ?? '');
      setSettlementFreeShippingMinQuantity(si?.freeShippingMinQuantity?.toString() ?? '');
      setSettlementCourierFee(si?.courierFee?.toString() ?? '');
      setSettlementCourierFreeMinQuantity(si?.courierFreeMinQuantity?.toString() ?? '');
      setSettlementDay(si?.settlementDay?.toString() ?? '');

      const tp = brandData.thanksPrint;
      setThanksEnabled(tp?.enabled ?? false);
      setThanksGuideText(tp?.guideText ?? '');
      const loaded = (tp?.templates ?? [])
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map<TemplateForm>((t) => ({
          key: t._id ?? genKey(),
          _id: t._id,
          name: t.name ?? '',
          templateImage: t.templateImage ?? null,
          file: null,
          preview: '',
          messagePresets: [...(t.messagePresets ?? [])],
          senderNameMaxLength: t.senderNameMaxLength?.toString() ?? '',
          messageBox: t.messageBox,
          senderBox: t.senderBox,
          enabled: t.enabled ?? true,
        }));
      setTemplates(loaded);
    }
  }, [isEditMode, brandData]);

  // 제출 핸들러
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      alert('브랜드명을 입력해주세요.');
      return;
    }

    if (thanksEnabled) {
      if (templates.length === 0) {
        alert('감사인쇄 사용 시 템플릿을 1개 이상 추가해주세요.');
        return;
      }
      if (templates.some((t) => !t.file && !t.templateImage)) {
        alert('모든 감사인쇄 템플릿에 이미지를 등록해주세요.');
        return;
      }
    }

    try {
      setIsSubmitting(true);

      const couponDetail =
        validPeriod.trim() || placeOfExchange.trim()
          ? {
              ...(validPeriod.trim() && { validPeriod: validPeriod.trim() }),
              ...(placeOfExchange.trim() && { placeOfExchange: placeOfExchange.trim() }),
            }
          : undefined;

      const filteredPhoneNumbers = notifyPhoneNumbers
        .map((p) => p.trim())
        .filter(Boolean);

      const hasDeliveryDetail =
        shippingInfo.trim() ||
        shippingFeeInfo.trim() ||
        shippingFee.trim() !== '' ||
        freeShippingMinQuantity.trim() !== '' ||
        filteredPhoneNumbers.length > 0;

      const deliveryDetail = hasDeliveryDetail
        ? {
            ...(shippingInfo.trim() && { shippingInfo: shippingInfo.trim() }),
            ...(shippingFeeInfo.trim() && { shippingFeeInfo: shippingFeeInfo.trim() }),
            ...(shippingFee.trim() !== '' && { shippingFee: Number(shippingFee) }),
            ...(freeShippingMinQuantity.trim() !== '' && {
              freeShippingMinQuantity: Number(freeShippingMinQuantity),
            }),
            ...(filteredPhoneNumbers.length > 0 && {
              notifyPhoneNumbers: filteredPhoneNumbers,
            }),
          }
        : undefined;

      // 감사인쇄 설정 구성 — 신규 파일은 업로드 후 id로 변환, order는 화면 순서대로
      const builtTemplates: ThanksPrintTemplate<string>[] = [];
      for (let i = 0; i < templates.length; i++) {
        const t = templates[i];
        if (!t) continue;
        let imageId = t.templateImage?._id;
        if (t.file) {
          const uploaded = await postImage(t.file, sellerRole());
          imageId = uploaded._id;
        }
        // 지정 문구: 완전히 빈 항목만 제거 — 내부 줄바꿈·들여쓰기는 인쇄 원본이라 보존
        const presets = t.messagePresets.filter((p) => p.trim().length > 0);
        const senderNameMaxLength = Number(t.senderNameMaxLength);
        builtTemplates.push({
          ...(t._id && { _id: t._id }),
          ...(t.name.trim() && { name: t.name.trim() }),
          ...(imageId && { templateImage: imageId }),
          ...(presets.length > 0 && { messagePresets: presets }),
          ...(t.senderNameMaxLength.trim() !== '' &&
            senderNameMaxLength > 0 && { senderNameMaxLength }),
          ...(t.messageBox && { messageBox: t.messageBox }),
          ...(t.senderBox && { senderBox: t.senderBox }),
          order: i,
          enabled: t.enabled,
        });
      }

      const thanksPrint: ThanksPrintConfig<string> = {
        enabled: thanksEnabled,
        templates: builtTemplates,
      };
      if (thanksGuideText.trim()) thanksPrint.guideText = thanksGuideText.trim();

      // 정산정보 — 관리자만 전송 (비관리자는 필드 자체를 payload에서 제외해 기존 값 보존)
      const settlementInfo: SettlementInfo | undefined = canEditSettlement
        ? {
            accountEntity: settlementAccountEntity,
            ...(settlementShippingFee.trim() !== '' && {
              shippingFee: Number(settlementShippingFee),
            }),
            ...(settlementFreeShippingMinQuantity.trim() !== '' && {
              freeShippingMinQuantity: Number(settlementFreeShippingMinQuantity),
            }),
            ...(settlementCourierFee.trim() !== '' && {
              courierFee: Number(settlementCourierFee),
            }),
            ...(settlementCourierFreeMinQuantity.trim() !== '' && {
              courierFreeMinQuantity: Number(settlementCourierFreeMinQuantity),
            }),
            ...(settlementDay.trim() !== '' && {
              settlementDay: Number(settlementDay),
            }),
          }
        : undefined;

      const payload = {
        name,
        couponDetail,
        deliveryDetail,
        allowIndividualDelivery,
        thanksPrint,
        ...(canEditSettlement && { settlementInfo }),
      };

      if (isEditMode && brandId) {
        const updated = await updateGiftItemBrand(brandId, payload, sellerRole());
        setListData((prev) =>
          prev.map((item) => (item._id === brandId ? updated : item)),
        );
        // PATCH 응답은 populate 미적용 → 캐시 무효화로 최신 populated 데이터를 재조회
        // (단건 캐시까지 무효화해야 재진입 시 폼이 옛 값으로 다시 로드되지 않음)
        await queryClient.invalidateQueries(giftItemBrandUrl(brandId, sellerRole()));
        await queryClient.invalidateQueries(giftItemBrandListUrl(sellerRole()));
        alert('수정되었습니다.');
      } else {
        const created = await createGiftItemBrand(payload, sellerRole());
        setListData((prev) => [...prev, created]);
        await queryClient.invalidateQueries(giftItemBrandListUrl(sellerRole()));
        alert('등록되었습니다.');
      }

      navigate('/gift-seller/item-brand');
    } catch (error) {
      console.error(error);
      alert(isEditMode ? '수정에 실패했습니다.' : '등록에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 로딩 중 (수정 모드)
  if (isEditMode && (status === 'loading' || status === 'idle')) {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-100"
          >
            ←
          </button>
          <h1 className="text-xl font-bold">
            상품 브랜드 {isEditMode ? '수정' : '등록'}
          </h1>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="mx-auto max-w-3xl p-4">
        <div className="rounded-lg bg-white p-4 shadow-sm">
          <div className="space-y-4">
            <TextField
              label="브랜드명"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="예: 스타벅스, 투썸플레이스"
              required
            />
            <p className="text-sm text-gray-500">
              상품의 브랜드를 입력해주세요. 예: 스타벅스, 투썸플레이스
            </p>
          </div>
        </div>

        <div className="mt-4 rounded-lg bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-base font-semibold">쿠폰 상세 정보 (선택)</h2>
          <div className="space-y-4">
            <TextField
              label="유효기간"
              value={validPeriod}
              onChange={(e) => setValidPeriod(e.target.value)}
              placeholder="예: 발행일로부터 93일"
            />
            <TextField
              label="교환처"
              value={placeOfExchange}
              onChange={(e) => setPlaceOfExchange(e.target.value)}
              placeholder="예: 전국 스타벅스 매장"
            />
            <p className="text-sm text-gray-500">
              쿠폰형 상품의 유효기간과 교환처 정보를 입력해주세요. 입력하지 않아도 됩니다.
            </p>
          </div>
        </div>

        <div className="mt-4 rounded-lg bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-base font-semibold">배송 상세 정보 (선택)</h2>
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                배송정보/기간 안내
              </label>
              <textarea
                value={shippingInfo}
                onChange={(e) => setShippingInfo(e.target.value)}
                placeholder="예: 주문 후 2~3일 이내 출고"
                className="h-20 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                배송비 안내
              </label>
              <textarea
                value={shippingFeeInfo}
                onChange={(e) => setShippingFeeInfo(e.target.value)}
                placeholder="예: 무료배송 / 3,000원"
                className="h-20 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  소량주문 추가금 (개당)
                </label>
                <input
                  type="number"
                  value={shippingFee}
                  onChange={(e) => setShippingFee(e.target.value)}
                  min={0}
                  placeholder="예: 500"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  추가금 면제 최소수량
                </label>
                <input
                  type="number"
                  value={freeShippingMinQuantity}
                  onChange={(e) => setFreeShippingMinQuantity(e.target.value)}
                  min={0}
                  placeholder="예: 20"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            </div>
            <p className="text-xs text-gray-400">
              이 수량 미만 주문 시 <b>개당</b> 추가금이 단가에 붙습니다(이상이면 면제).
              상품에 별도 설정이 없으면 이 값을 따릅니다.
            </p>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                배송 알림 수신 전화번호 (최대 3개)
              </label>
              <div className="space-y-2">
                {notifyPhoneNumbers.map((phone, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => {
                        const updated = [...notifyPhoneNumbers];
                        updated[index] = e.target.value;
                        setNotifyPhoneNumbers(updated);
                      }}
                      placeholder="예: 01012345678"
                      className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                    {notifyPhoneNumbers.length > 1 && (
                      <button
                        type="button"
                        onClick={() =>
                          setNotifyPhoneNumbers(
                            notifyPhoneNumbers.filter((_, i) => i !== index),
                          )
                        }
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-red-300 text-red-500 hover:bg-red-50"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {notifyPhoneNumbers.length < 3 && (
                <button
                  type="button"
                  onClick={() => setNotifyPhoneNumbers([...notifyPhoneNumbers, ''])}
                  className="mt-2 rounded-lg border border-dashed border-gray-300 px-3 py-1.5 text-sm text-gray-500 hover:border-indigo-400 hover:text-indigo-600"
                >
                  + 번호 추가
                </button>
              )}
              <p className="mt-1 text-xs text-gray-400">
                배송지 입력/변경 시 SMS 알림을 받을 전화번호를 입력하세요.
              </p>
            </div>
            <div className="flex items-start justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div>
                <label className="text-sm font-medium text-gray-700">개별배송 허용</label>
                <p className="mt-0.5 text-xs text-gray-500">
                  끄면 이 브랜드 상품은 일괄배송(Bulk) 전용이 됩니다. 상품에 별도 설정이
                  없으면 이 값을 따릅니다.
                </p>
              </div>
              <input
                type="checkbox"
                checked={allowIndividualDelivery}
                onChange={(e) => setAllowIndividualDelivery(e.target.checked)}
                className="mt-0.5 h-5 w-5 accent-indigo-600"
              />
            </div>
            <p className="text-sm text-gray-500">
              배송형 상품의 배송정보를 입력해주세요. 이 브랜드의 모든 상품에 기본
              적용됩니다.
            </p>
          </div>
        </div>

        {/* 정산정보 (관리자 전용) */}
        {canEditSettlement && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 shadow-sm">
            <h2 className="mb-1 text-base font-semibold text-amber-800">
              정산정보 (관리자 전용)
            </h2>
            <p className="mb-3 text-sm text-amber-700">
              플랫폼↔공급사 정산용 단가입니다. 구매자에게 노출되지 않습니다.
            </p>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  정산 주체
                </label>
                <select
                  value={settlementAccountEntity}
                  onChange={(e) =>
                    setSettlementAccountEntity(e.target.value as AccountEntity)
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value={AccountEntity.NotDecided}>미정</option>
                  <option value={AccountEntity.Mari}>마리</option>
                  <option value={AccountEntity.Gaon}>가온</option>
                </select>
                <p className="mt-1 text-xs text-gray-400">
                  이 공급사의 도매가를 정산하는 주체입니다.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    소량주문 추가단가 (개당)
                  </label>
                  <input
                    type="number"
                    value={settlementShippingFee}
                    onChange={(e) => setSettlementShippingFee(e.target.value)}
                    min={0}
                    placeholder="예: 1000"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    추가단가 면제 최소수량
                  </label>
                  <input
                    type="number"
                    value={settlementFreeShippingMinQuantity}
                    onChange={(e) => setSettlementFreeShippingMinQuantity(e.target.value)}
                    min={0}
                    placeholder="예: 20"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-400">
                주문 수량이 이 값 미만이면 <b>개당</b> 추가단가를 정산 때
                청구합니다(이상이면 면제).
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    택배비 (건당)
                  </label>
                  <input
                    type="number"
                    value={settlementCourierFee}
                    onChange={(e) => setSettlementCourierFee(e.target.value)}
                    min={0}
                    placeholder="예: 4500"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    택배비 무료 최소수량
                  </label>
                  <input
                    type="number"
                    value={settlementCourierFreeMinQuantity}
                    onChange={(e) => setSettlementCourierFreeMinQuantity(e.target.value)}
                    min={0}
                    placeholder="예: 50"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-400">
                주문 수량이 이 값 미만이면 <b>건당</b> 택배비를 정산 때
                청구합니다(이상이면 무료).
              </p>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  정산일 (익월 기준)
                </label>
                <input
                  type="number"
                  value={settlementDay}
                  onChange={(e) => setSettlementDay(e.target.value)}
                  min={1}
                  max={31}
                  placeholder="예: 15"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
                <p className="mt-1 text-xs text-gray-400">
                  익월 며칠에 정산할지 일자를 입력하세요. 예: 15 → 익월 15일
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 감사메시지 인쇄 설정 */}
        <div className="mt-4 rounded-lg bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold">감사메시지 인쇄 (선택)</h2>
            <label className="flex cursor-pointer items-center gap-2">
              <span className="text-sm text-gray-600">
                {thanksEnabled ? '사용' : '미사용'}
              </span>
              <input
                type="checkbox"
                checked={thanksEnabled}
                onChange={(e) => setThanksEnabled(e.target.checked)}
                className="h-5 w-5 accent-indigo-600"
              />
            </label>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            이 브랜드 상품을 보낼 때 감사글/상주명을 인쇄해 발송하는 경우 사용하세요.
          </p>

          {thanksEnabled && (
            <div className="mt-4 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  안내 문구 (공통)
                </label>
                <textarea
                  value={thanksGuideText}
                  onChange={(e) => setThanksGuideText(e.target.value)}
                  placeholder="예: 위와 같은 형태로 인쇄되어 발송됩니다."
                  className="h-16 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>

              {/* 인쇄 템플릿 목록 (구매자가 이 중 하나 선택) */}
              {templates.map((t, idx) => {
                const displayUrl = templateDisplayUrl(t);
                return (
                  <div
                    key={t.key}
                    className={`rounded-lg border p-3 ${
                      t.enabled
                        ? 'border-gray-200'
                        : 'border-gray-200 bg-gray-50 opacity-70'
                    }`}
                  >
                    <div className="mb-2 flex items-center gap-2">
                      <span className="rounded bg-indigo-100 px-2 py-0.5 text-xs font-bold text-indigo-700">
                        템플릿 {idx + 1}
                      </span>
                      <input
                        type="text"
                        value={t.name}
                        onChange={(e) => updateTemplate(t.key, { name: e.target.value })}
                        placeholder="템플릿 이름 (예: 가로형)"
                        className="flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm"
                      />
                      <label className="flex shrink-0 cursor-pointer items-center gap-1 text-xs text-gray-600">
                        <input
                          type="checkbox"
                          checked={t.enabled}
                          onChange={(e) =>
                            updateTemplate(t.key, { enabled: e.target.checked })
                          }
                          className="h-4 w-4 accent-indigo-600"
                        />
                        노출
                      </label>
                      <button
                        type="button"
                        onClick={() => removeTemplate(t.key)}
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-red-300 text-red-500 hover:bg-red-50"
                      >
                        ✕
                      </button>
                    </div>

                    <ImageUploadBox
                      title="인쇄 템플릿 이미지"
                      setFile={(f: File) => handleTemplateFile(t.key, f)}
                      defaultImageUrl={t.templateImage?.url}
                    />

                    {displayUrl ? (
                      <div className="mt-3">
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          인쇄 영역 지정
                        </label>
                        <ThanksPrintBoxEditor
                          imageUrl={displayUrl}
                          messageBox={t.messageBox}
                          senderBox={t.senderBox}
                          onChange={(boxKey, box) => handleBoxChange(t.key, boxKey, box)}
                        />
                      </div>
                    ) : (
                      <p className="mt-3 rounded-lg bg-gray-50 px-3 py-4 text-center text-sm text-gray-400">
                        템플릿 이미지를 업로드하면 인쇄 영역을 지정할 수 있습니다.
                      </p>
                    )}

                    {/* 감사글 지정 문구 세트 (택1 강제) */}
                    <div className="mt-3 rounded-lg border border-stone-200 bg-stone-50 p-3">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-gray-700">
                          감사글 지정 문구 세트
                        </label>
                        <button
                          type="button"
                          onClick={() => addPreset(t.key)}
                          className="rounded-md border border-dashed border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-500 hover:border-indigo-400 hover:text-indigo-600"
                        >
                          + 문구 추가
                        </button>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        문구를 등록하면 구매자는 <b>이 중 하나만 선택</b>할 수 있고
                        자유입력이 막힙니다. 비워두면 기존처럼 자유입력이 허용됩니다.
                        줄바꿈·들여쓰기까지 그대로 인쇄됩니다.
                      </p>
                      {t.messagePresets.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {t.messagePresets.map((preset, pi) => (
                            <div key={pi} className="flex items-start gap-2">
                              <textarea
                                value={preset}
                                onChange={(e) => updatePreset(t.key, pi, e.target.value)}
                                placeholder={'예: 감사합니다.'}
                                rows={2}
                                className="min-h-[3rem] flex-1 whitespace-pre rounded-md border border-gray-300 px-2 py-1.5 font-mono text-sm"
                              />
                              <button
                                type="button"
                                onClick={() => removePreset(t.key, pi)}
                                className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-red-300 text-red-500 hover:bg-red-50"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="mt-3">
                        <label className="mb-0.5 block text-xs font-medium text-gray-600">
                          보내는 분 글자수 제한
                        </label>
                        <input
                          type="number"
                          value={t.senderNameMaxLength}
                          onChange={(e) =>
                            updateTemplate(t.key, { senderNameMaxLength: e.target.value })
                          }
                          min={1}
                          placeholder="예: 10 (비우면 제한 없음)"
                          className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                        />
                        <p className="mt-1 text-xs text-gray-400">
                          인쇄되는 발신문구 <b>전체</b>(&quot;홍길동 배상&quot; = 6자)
                          기준입니다. 예: 10으로 설정하면 이름은 최대 7자까지 입력됩니다.
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}

              <button
                type="button"
                onClick={addTemplate}
                className="w-full rounded-lg border border-dashed border-gray-300 py-2.5 text-sm font-medium text-gray-500 hover:border-indigo-400 hover:text-indigo-600"
              >
                + 템플릿 추가
              </button>
              {templates.length === 0 && (
                <p className="text-center text-xs text-gray-400">
                  최소 1개의 템플릿을 추가하세요. 구매자가 이 중 하나를 선택합니다.
                </p>
              )}
            </div>
          )}
        </div>

        {/* 버튼 */}
        <div className="mt-4 flex gap-3">
          <Button
            type="button"
            onClick={() => navigate(-1)}
            className="flex-1 rounded-lg border border-gray-300 bg-white py-3 font-medium text-gray-700"
          >
            취소
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 rounded-lg bg-indigo-600 py-3 font-medium text-white disabled:opacity-50"
          >
            {isSubmitting ? '저장 중...' : isEditMode ? '수정하기' : '등록하기'}
          </Button>
        </div>
      </form>
    </div>
  );
};
